import React, { useState, useRef } from 'react';
import { View, StyleSheet, TextInput, FlatList, Platform, ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { AppRoute } from '_/navigation/types';
import { hostApi } from '_/services/api';
import { useVisit } from '_/hooks/VisitContext';
import { useNextCheckinScreen } from '_/hooks/useNextCheckinScreen';
import { colors, fontSizes } from '_/config/theme';
import { Text, Input, Header } from '_/components';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import KeyboardContainer from '_/components/KeyboardContainer';
import DataCell from '_/components/HostsVisitsFlatList/DataCell';
import { MeetingHostsModel } from '@w3lcome/types';

const CoHostSelectScreen: React.FC = () => {
  const { visit, setVisit } = useVisit();
  const { t } = useTranslation();
  const { goToNextScreen } = useNextCheckinScreen(AppRoute.CHECKIN_HOST);

  const [textSearch, setTextSearch] = useState('');
  const [isFocusedInput, setIsFocusedInput] = useState(false);
  const inputSearchRef = useRef<TextInput>(null);

  const { data: coHosts = [], isLoading } = useQuery({
    queryKey: ['meetingHosts', visit.meetingId],
    enabled: !!visit.meetingId,
    staleTime: 60 * 2000,
    queryFn: async () => {
      if (!visit.meetingId) return [];
      const { data } = await hostApi.getMeetingHosts(visit.meetingId);
      return data || [];
    },
  });

  const handleSelectHost = (host: MeetingHostsModel) => {
    setVisit({ coHost: host });
    goToNextScreen();
  };

  const filteredCoHosts = textSearch
    ? coHosts.filter((item) => item.host?.name?.toLowerCase().includes(textSearch.toLowerCase()))
    : coHosts;

  return (
    <KeyboardContainer keyboardOpen={isFocusedInput} inputName="search" keyboardType="default">
      <View style={styles.container}>
        <ContainerWidthLimit>
          <Header />

          <View style={styles.content}>
            <View style={styles.innerContent}>
              <View style={styles.headerText}>
                <Text center size={fontSizes.xl}>
                  {t('hostScreen.Greeting', { defaultValue: 'Olá' })}
                  <Text center fontType="bold" size={fontSizes.xl}>
                    {` ${visit.name || ''} `}
                  </Text>
                  {t('hostScreen.Message', { defaultValue: 'por favor selecione seu anfitrião' })}
                </Text>
              </View>

              <View style={styles.searchContent}>
                <Input
                  icon="search"
                  autoCapitalize="words"
                  label={t('hostScreen.Search', { defaultValue: 'Busque pelo nome do anfitrião' })}
                  ref={inputSearchRef}
                  value={textSearch}
                  onChangeText={setTextSearch}
                  onFocus={() => setIsFocusedInput(true)}
                  onBlur={() => setIsFocusedInput(false)}
                />
              </View>

              {isLoading && (
                <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: 40 }} />
              )}

              {!isLoading && filteredCoHosts.length > 0 && (
                <View
                  style={{
                    flex: 1,
                    marginTop: 24,
                    paddingBottom: isFocusedInput ? 0 : 48,
                  }}
                >
                  <FlatList
                    style={StyleSheet.absoluteFill}
                    keyboardShouldPersistTaps="always"
                    data={filteredCoHosts}
                    keyExtractor={(item) => item.hostId}
                    showsVerticalScrollIndicator={Platform.OS === 'web'}
                    renderItem={({ item }) => (
                      <DataCell
                        data={item.host}
                        isHosts={true}
                        onPress={() => handleSelectHost(item)}
                      />
                    )}
                  />
                </View>
              )}

              {!isLoading && filteredCoHosts.length === 0 && textSearch !== '' && (
                <View style={styles.hostNotFound}>
                  <Text center size={fontSizes.xl2}>
                    {t('hostScreen.couldNotFindHost', { defaultValue: 'Não conseguimos encontrar ninguém com' })}
                  </Text>
                  <Text center fontType="bold" size={fontSizes.xl3} style={styles.hostNotFoundName}>
                    {textSearch}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </ContainerWidthLimit>
      </View>
    </KeyboardContainer>
  );
};

export default CoHostSelectScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
    alignItems: 'center',
  },
  content: {
    flex: 1,
    alignItems: 'center',
  },
  innerContent: {
    flex: 1,
    width: '80%',
  },
  headerText: {
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  searchContent: {
    marginVertical: 32,
  },
  hostNotFound: {
    flex: 1,
    alignSelf: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },
  hostNotFoundButton: {
    marginTop: 24,
  },
  hostNotFoundName: {
    marginVertical: 12,
  },
});
